import React, { useEffect, useRef, useState } from 'react';
import { Users, Loader2, IndianRupee, Briefcase, ShieldCheck, ArrowRightLeft, MapPin, X, Camera, Upload, Trash2 } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { useApp } from '../../context/AppContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token') || localStorage.getItem('c24_admin_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const AdminWorkers = () => {
  const { user } = useApp();
  const isSuperAdmin = user?.role === 'admin';

  const [data, setData] = useState(null);
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);

  // Transfer modal state
  const [xferWorker, setXferWorker] = useState(null);
  const [xferTarget, setXferTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  // Photo upload state (per-worker)
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRef = useRef(null);
  const [pendingWorker, setPendingWorker] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [w, f] = await Promise.all([
        axios.get(`${API}/admin/workers`, authCfg()),
        axios.get(`${API}/franchises`, authCfg()).catch(() => ({ data: [] })),
      ]);
      setData(w.data);
      setFranchises(f.data || []);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // ---------- Photo upload ----------
  const MAX_BYTES = 2 * 1024 * 1024; // 2 MB
  const triggerUpload = (worker) => {
    setPendingWorker(worker);
    setMsg(null);
    fileInputRef.current?.click();
  };
  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file || !pendingWorker) return;
    if (!/^image\/(jpeg|png|webp|gif)$/i.test(file.type)) {
      setMsg({ ok: false, text: 'Only JPEG, PNG, WEBP or GIF images are allowed.' });
      return;
    }
    if (file.size > MAX_BYTES) {
      setMsg({ ok: false, text: 'Image is larger than 2 MB. Please crop or resize first.' });
      return;
    }
    const dataUri = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result); r.onerror = rej;
      r.readAsDataURL(file);
    });
    setUploadingId(pendingWorker.user_id);
    try {
      const { data: r } = await axios.post(
        `${API}/admin/workers/${pendingWorker.user_id}/photo`,
        { picture: dataUri }, authCfg(),
      );
      // Optimistic update in-place
      setData((d) => d && ({
        ...d,
        workers: d.workers.map((w) => (w.user_id === r.user_id ? { ...w, picture: r.picture } : w)),
      }));
      setMsg({ ok: true, text: `Photo updated for ${pendingWorker.name || pendingWorker.email}` });
    } catch (err) {
      setMsg({ ok: false, text: err?.response?.data?.detail || 'Upload failed' });
    } finally {
      setUploadingId(null);
      setPendingWorker(null);
    }
  };
  const deletePhoto = async (worker) => {
    if (!window.confirm(`Remove headshot for ${worker.name || worker.email}?`)) return;
    setUploadingId(worker.user_id);
    try {
      await axios.delete(`${API}/admin/workers/${worker.user_id}/photo`, authCfg());
      setData((d) => d && ({
        ...d,
        workers: d.workers.map((w) => (w.user_id === worker.user_id ? { ...w, picture: '' } : w)),
      }));
      setMsg({ ok: true, text: 'Photo removed' });
    } catch (err) {
      setMsg({ ok: false, text: err?.response?.data?.detail || 'Delete failed' });
    } finally { setUploadingId(null); }
  };

  const openTransfer = (w) => {
    setXferWorker(w);
    setXferTarget('');
    setMsg(null);
  };
  const submitTransfer = async () => {
    if (!xferWorker || !xferTarget) { setMsg({ ok: false, text: 'Pick a target franchise' }); return; }
    if (xferTarget === xferWorker.franchise_id) { setMsg({ ok: false, text: 'Worker is already in this franchise' }); return; }
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/franchises/staff-transfer`, {
        user_id: xferWorker.user_id, target_franchise_id: xferTarget,
      }, authCfg());
      setMsg({ ok: true, text: `Moved ${xferWorker.name || xferWorker.email} to ${r.district}` });
      setXferWorker(null);
      load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Transfer failed' });
    } finally { setBusy(false); }
  };

  return (
    <AdminLayout title="Worker Management">
      {loading ? (
        <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>
      ) : (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={onFileChosen}
            className="hidden"
            data-testid="worker-photo-file-input"
          />
          {msg && (
            <div data-testid="admin-workers-msg" className={`mb-4 text-sm rounded-lg px-3 py-2 ${msg.ok ? 'bg-[#E8F5E9] text-[#1B5E20]' : 'bg-red-50 text-red-700'}`}>{msg.text}</div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            <Stat label="Total workers" value={data.summary.total_workers} icon={Users} color="#2E7D32" bg="#E8F5E9" testid="admin-workers-total" />
            <Stat label="Active right now" value={data.summary.active_now} icon={Briefcase} color="#D97706" bg="#FEF3C7" testid="admin-workers-active" />
            <Stat label="Total earnings paid" value={`₹${data.summary.total_earnings_paid.toLocaleString('en-IN')}`} icon={IndianRupee} color="#1E88E5" bg="#DBEAFE" testid="admin-workers-earnings" />
          </div>

          <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden" data-testid="admin-workers-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
                  <tr>
                    <Th>Worker</Th><Th>Phone</Th><Th>District</Th><Th>Finished</Th><Th>Active</Th><Th className="text-right">Earned</Th>{isSuperAdmin && <Th className="text-right">Action</Th>}
                  </tr>
                </thead>
                <tbody>
                  {data.workers.map((w) => (
                    <tr key={w.user_id} data-testid={`admin-worker-${w.user_id}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <WorkerAvatar
                            worker={w}
                            uploading={uploadingId === w.user_id}
                            onUpload={() => triggerUpload(w)}
                            onDelete={() => deletePhoto(w)}
                          />
                          <div className="min-w-0">
                            <p className="font-semibold truncate">{w.name || '—'}</p>
                            <p className="text-xs text-neutral-500 truncate">{w.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-neutral-600">{w.phone || '—'}</td>
                      <td className="px-4 py-3">
                        {w.district ? (
                          <span className="inline-flex items-center gap-1 text-xs text-neutral-700"><MapPin className="w-3 h-3 text-[#2E7D32]" />{w.district}</span>
                        ) : <span className="text-xs text-neutral-400">Unassigned</span>}
                      </td>
                      <td className="px-4 py-3"><Pill color="#2E7D32" bg="#E8F5E9">{w.finished_count}</Pill></td>
                      <td className="px-4 py-3"><Pill color={w.active_count > 0 ? '#D97706' : '#6B7280'} bg={w.active_count > 0 ? '#FEF3C7' : '#F3F4F6'}>{w.active_count}</Pill></td>
                      <td className="px-4 py-3 text-right font-bold text-[#2E7D32]">₹{w.total_earned.toLocaleString('en-IN')}</td>
                      {isSuperAdmin && (
                        <td className="px-4 py-3 text-right">
                          <button
                            data-testid={`admin-worker-transfer-${w.user_id}`}
                            onClick={() => openTransfer(w)}
                            className="inline-flex items-center gap-1 bg-white border border-neutral-200 hover:border-[#2E7D32] hover:text-[#2E7D32] text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors">
                            <ArrowRightLeft className="w-3.5 h-3.5" /> Transfer
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {data.workers.length === 0 && (
                    <tr><td colSpan={isSuperAdmin ? 7 : 6} className="text-center text-neutral-500 py-8">No cleaner accounts yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-neutral-500 flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#2E7D32]" /> Data pulled directly from the shared users + bookings collections.</p>
        </>
      )}

      {xferWorker && (
        <Modal onClose={() => setXferWorker(null)} title="Transfer worker to another franchise">
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 mb-3">
            <p className="text-sm font-semibold text-neutral-900" data-testid="xfer-worker-name">{xferWorker.name || xferWorker.email}</p>
            <p className="text-xs text-neutral-500">{xferWorker.email}</p>
            <p className="text-xs mt-1 flex items-center gap-1"><MapPin className="w-3 h-3 text-[#2E7D32]" /> Currently: <span className="font-semibold">{xferWorker.district || 'Unassigned'}</span> <span className="font-mono text-neutral-500">({xferWorker.franchise_id || '—'})</span></p>
          </div>
          <label className="block text-xs font-semibold text-neutral-600">Move to franchise</label>
          <select
            data-testid="xfer-target-select"
            value={xferTarget}
            onChange={(e) => setXferTarget(e.target.value)}
            className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm">
            <option value="">Select target franchise</option>
            {franchises.filter(f => f.franchise_id !== xferWorker.franchise_id).map((f) => (
              <option key={f.franchise_id} value={f.franchise_id}>{f.district} ({f.franchise_id})</option>
            ))}
          </select>
          {franchises.length === 0 && (
            <p className="mt-2 text-xs text-red-600">No other franchises available. Create one from Franchises page first.</p>
          )}
          <button
            data-testid="xfer-confirm-btn"
            onClick={submitTransfer}
            disabled={busy || !xferTarget}
            className="mt-4 w-full py-2.5 rounded-lg bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-50 text-white font-semibold text-sm">
            {busy ? 'Transferring…' : 'Confirm transfer'}
          </button>
        </Modal>
      )}
    </AdminLayout>
  );
};

const Stat = ({ label, value, icon: Icon, color, bg, testid }) => (
  <div data-testid={testid} className="bg-white border border-neutral-200 rounded-xl p-4">
    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: bg }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <p className="mt-3 text-xs text-neutral-500 font-medium">{label}</p>
    <p className="font-extrabold text-2xl mt-0.5">{value}</p>
  </div>
);
const Th = ({ children, className = '' }) => <th className={`px-4 py-2.5 text-xs font-bold text-neutral-500 uppercase tracking-wider ${className}`}>{children}</th>;
const Pill = ({ children, color, bg }) => <span className="text-[11px] font-bold px-2 py-0.5 rounded" style={{ color, background: bg }}>{children}</span>;
const Modal = ({ children, onClose, title }) => (
  <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-5">
      <div className="flex items-center justify-between mb-4">
        <p className="font-extrabold">{title}</p>
        <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900" data-testid="xfer-modal-close"><X className="w-5 h-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

const WorkerAvatar = ({ worker, uploading, onUpload, onDelete }) => {
  const initials = ((worker.name || worker.email || '?').split(/\s+/).map((s) => s[0]).slice(0, 2).join('') || '?').toUpperCase();
  const hasPhoto = !!worker.picture;
  return (
    <div
      className="relative w-12 h-12 rounded-full overflow-hidden shrink-0 group cursor-pointer border-2 border-neutral-200 hover:border-[#2E7D32] transition-colors"
      onClick={onUpload}
      title={hasPhoto ? 'Click to replace photo' : 'Click to upload headshot'}
      data-testid={`worker-avatar-${worker.user_id}`}>
      {hasPhoto ? (
        <img src={worker.picture} alt={worker.name} className="w-full h-full object-cover" data-testid={`worker-photo-${worker.user_id}`} />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#E8F5E9] to-[#FFF3E0] text-[#2E7D32] font-bold text-sm">
          {initials}
        </div>
      )}
      {uploading && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <Loader2 className="w-4 h-4 text-white animate-spin" />
        </div>
      )}
      {!uploading && (
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
          <Camera className="w-4 h-4 text-white" />
        </div>
      )}
      {hasPhoto && !uploading && (
        <button
          data-testid={`worker-photo-remove-${worker.user_id}`}
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="absolute -bottom-1 -right-1 bg-white border border-neutral-200 rounded-full p-0.5 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:border-red-300 shadow-sm transition-all"
          title="Remove photo">
          <Trash2 className="w-3 h-3 text-red-600" />
        </button>
      )}
    </div>
  );
};

export default AdminWorkers;
